import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, MapPin, Minus, Plus, Search, Users } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { http, toISO, fmtDate, stayQuery, addDays } from "@/lib/api";
import { parseISO, startOfDay } from "date-fns";
import { useT } from "@/lib/i18n";

const Counter = ({ label, value, min, max, onChange, testid }) => (
  <div className="flex items-center justify-between py-2">
    <span className="text-sm font-medium text-slate-700">{label}</span>
    <div className="flex items-center gap-3">
      <button type="button" data-testid={`${testid}-minus`} disabled={value <= min} onClick={() => onChange(value - 1)}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-[#5392F9] transition-colors hover:bg-blue-50 disabled:opacity-30">
        <Minus className="h-4 w-4" />
      </button>
      <span data-testid={`${testid}-value`} className="w-5 text-center font-semibold">{value}</span>
      <button type="button" data-testid={`${testid}-plus`} disabled={value >= max} onClick={() => onChange(value + 1)}
        className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-300 text-[#5392F9] transition-colors hover:bg-blue-50 disabled:opacity-30">
        <Plus className="h-4 w-4" />
      </button>
    </div>
  </div>
);

const Field = ({ icon: Icon, label, children }) => (
  <div className="flex min-w-0 flex-1 items-center gap-3 px-4 py-3">
    <Icon className="h-5 w-5 shrink-0 text-slate-400" />
    <div className="min-w-0 flex-1 text-left">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
      {children}
    </div>
  </div>
);

export default function SearchBar({ initial, compact = false, target = null }) {
  const nav = useNavigate();
  const t = useT();
  const [s, setS] = useState(initial);
  const [dests, setDests] = useState([]);
  useEffect(() => { http.get("/public/destinations").then((r) => setDests(r.data)).catch(() => {}); }, []);
  useEffect(() => { setS(initial); }, [initial.check_in, initial.check_out, initial.city, initial.guests, initial.rooms]); // eslint-disable-line

  const onRange = (r) => {
    if (!r?.from) return;
    const ci = toISO(r.from);
    const co = r.to && toISO(r.to) > ci ? toISO(r.to) : addDays(ci, 1);
    setS({ ...s, check_in: ci, check_out: co });
  };
  const submit = (e) => { e.preventDefault(); nav(target ? `${target}?${stayQuery({ ...s, city: "" })}` : `/search?${stayQuery(s)}`); };

  return (
    <form onSubmit={submit} data-testid="search-bar"
      className={`flex flex-col divide-y divide-slate-200 overflow-visible rounded-2xl border border-slate-200 bg-white lg:flex-row lg:items-center lg:divide-x lg:divide-y-0 ${compact ? "shadow-sm" : "shadow-2xl"}`}>
      {!target && <Field icon={MapPin} label={t("Tujuan / nama hotel")}>
        <input data-testid="search-destination-input" list="dest-list" value={s.city} placeholder={t("Mau ke mana? mis. Bali")}
          onChange={(e) => setS({ ...s, city: e.target.value })}
          className="w-full bg-transparent text-base font-semibold text-slate-900 outline-none placeholder:font-normal placeholder:text-slate-400" />
        <datalist id="dest-list">{dests.map((d) => <option key={d.city} value={d.city}>{d.count} properti</option>)}</datalist>
      </Field>}
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" data-testid="search-dates-trigger" className="flex-1 text-left transition-colors hover:bg-slate-50">
            <Field icon={CalendarDays} label={t("Check-in — Check-out")}>
              <p className="truncate font-semibold text-slate-900">{fmtDate(s.check_in, "d MMM")} — {fmtDate(s.check_out, "d MMM yyyy")}</p>
            </Field>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar data-testid="search-calendar" mode="range" numberOfMonths={2} defaultMonth={parseISO(s.check_in)}
            selected={{ from: parseISO(s.check_in), to: parseISO(s.check_out) }} onSelect={onRange}
            disabled={{ before: startOfDay(new Date()) }} />
        </PopoverContent>
      </Popover>
      <Popover>
        <PopoverTrigger asChild>
          <button type="button" data-testid="search-guests-trigger" className="flex-1 text-left transition-colors hover:bg-slate-50">
            <Field icon={Users} label={t("Tamu & kamar")}>
              <p className="truncate font-semibold text-slate-900">{s.guests} {t("dewasa")} · {s.rooms} {t("kamar")}</p>
            </Field>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72" align="start">
          <Counter label="Kamar" testid="rooms" value={s.rooms} min={1} max={10} onChange={(v) => setS({ ...s, rooms: v, guests: Math.max(s.guests, v) })} />
          <Counter label="Dewasa" testid="guests" value={s.guests} min={1} max={30} onChange={(v) => setS({ ...s, guests: v })} />
        </PopoverContent>
      </Popover>
      <div className="p-2">
        <button type="submit" data-testid="search-submit-button"
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#5392F9] px-8 py-3.5 font-heading font-bold text-white transition-colors hover:bg-[#3B7BE6] lg:w-auto">
          <Search className="h-5 w-5" /> {t("CARI")}
        </button>
      </div>
    </form>
  );
}
